import Section from "@bmi-digital/components/section";
import TableOfContent from "@bmi-digital/components/table-of-content";
import { graphql } from "gatsby";
import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import BackToResults from "../../../components/BackToResults";
import Breadcrumbs from "../../../components/Breadcrumbs";
import LeadBlockSection from "../../../components/LeadBlockSection";
import LinkColumnsSection from "../../../components/LinkColumnsSection";
import Page from "../../../components/Page";
import Sections from "../../../components/Sections";
import ShareWidgetSection from "../../../components/ShareWidgetSection";
import NextBestActions from "../../../components/next-best-actions/NextBestActions";
import { useConfig } from "../../../contexts/ConfigProvider";
import { updateBreadcrumbTitleFromPayload } from "../../../utils/breadcrumbUtils";
import {
  generateHeroLevel,
  generateHeroProps
} from "../../../utils/heroLevelUtils";
import { renderHero } from "../../../utils/heroTypesUI";
import Protected from "../../../pages/protected";
import { SignupBlockSectionData } from "../../../components/SignupBlock";
import { getPathWithCountryCode } from "../../../utils/path";
import SampleBasketSection from "../../../components/SampleBasketSection";
import {
  HomePageData,
  SiteData,
  useSiteContext
} from "../../../components/Site";
import type { NextBestActionsData } from "../../../components/next-best-actions/NextBestActions";
import type { ShareWidgetSectionData } from "../../../components/ShareWidgetSection";
import type { PageData } from "../../../components/Page";
import type { LinkColumnsSectionData as LinkColumnsSectionData } from "../../../components/LinkColumnsSection";
import type { LeadBlockData } from "../../../components/LeadBlockSection";
import type { LinkData } from "../../../components/link/types";
import type { SimplePageInfoHero } from "../../../components/PageInfo";
import type { SpotlightHeroProps } from "@bmi-digital/components/spotlight-hero";
import type { HeroProps } from "@bmi-digital/components/hero";
import type { PayloadSimplePage } from "../../../types/payload";
import type { MicroCopy as MicroCopyData } from "../../../components/MicroCopy";
import type { CalculatorConfig } from "../../../components/pitched-roof-calculator/types";
import type { HouseType } from "../../../components/visualiser/Types";
import type { GeneralErrorData } from "../../general-error";
import type {
  CountryNavigation,
  SampleOrder
} from "../../../components/Header";
import type { SampleBasketSectionData } from "../../../components/SampleBasketBase";
import type { Visualiser } from "../../../components/Visualiser";
import type { CardCollectionSectionData } from "../../../components/CardCollectionSection";
import type { CarouselSectionData } from "../../../components/carousel-section/CarouselSection";
import type { DocumentDownloadSectionData } from "../../../components/DocumentDownloadSection";
import type { EmbeddedScriptSectionData } from "../../../components/EmbeddedScriptSection";
import type { ExploreBarSectionData } from "../../../components/ExploreBarSection";
import type { FormSectionData } from "../../../components/FormSection";
import type { IframeSectionData } from "../../../components/IframeSection";
import type { MediaGallerySectionData } from "../../../components/MediaGallerySection";
import type { PromoSectionData } from "../../../components/PromoSection";
import type { ServiceLocatorSectionData } from "../../../components/service-locator-section";
import type { SyndicateSectionData } from "../../../components/SyndicateSection";
import type { SystemConfiguratorSectionData } from "../../../components/SystemConfiguratorSection";
import type { TabsOrAccordionSectionData } from "../../../components/TabsOrAccordionSection";
import type { TeamSectionData } from "../../../components/TeamSection";
import type { TitleWithContentSectionData } from "../../../components/TitleWithContentSection";
import type { VideoSectionData } from "../../../components/VideoSection";

export type SimplePageData = PageData &
  SimplePageInfoHero &
  Pick<
    PayloadSimplePage,
    "breadcrumbTitle" | "heroType" | "isSimplePageProtected" | "date"
  > & {
    _id: string;
    cta: readonly [LinkData] | null;
    leadBlock: readonly [LeadBlockData] | null;
    shareWidget: readonly [ShareWidgetSectionData] | null;
    sections:
      | readonly (
          | CardCollectionSectionData
          | CarouselSectionData
          | DocumentDownloadSectionData
          | EmbeddedScriptSectionData
          | ExploreBarSectionData
          | FormSectionData
          | IframeSectionData
          | MediaGallerySectionData
          | PromoSectionData
          | ServiceLocatorSectionData
          | SignupBlockSectionData
          | SyndicateSectionData
          | SystemConfiguratorSectionData
          | TabsOrAccordionSectionData
          | TeamSectionData
          | TitleWithContentSectionData
          | VideoSectionData
        )[]
      | null;
    nextBestActions: readonly { value: NextBestActionsData }[] | null;
    linkColumns: readonly [LinkColumnsSectionData] | null;
  };

export type Props = {
  data: {
    payloadSimplePage: SimplePageData;
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadHomePage: HomePageData;
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: (SampleOrder & SampleBasketSectionData) | null;
    payloadVisualiser: Visualiser | null;
  };
  pageContext: {
    pageId: string;
    variantCodeToPathMap?: Record<string, string>;
  };
};

const SimplePage = ({ data, pageContext }: Props) => {
  const {
    _id,
    brandLogo,
    title,
    subtitle,
    featuredMedia,
    leadBlock,
    shareWidget,
    sections,
    nextBestActions,
    linkColumns,
    heroType,
    breadcrumbs,
    breadcrumbTitle,
    meta,
    featuredVideo,
    cta,
    signupBlock,
    isSimplePageProtected,
    path
  } = data.payloadSimplePage;
  const enhancedBreadcrumbs = updateBreadcrumbTitleFromPayload(
    breadcrumbs,
    breadcrumbTitle
  );
  const { isBrandProviderEnabled } = useConfig();
  const { countryCode } = useSiteContext();
  const theme = useTheme();
  const isSampleOrderPage = useMemo(() => {
    if (!data.payloadSampleOrder) {
      return false;
    }

    return (
      getPathWithCountryCode(countryCode, data.payloadSampleOrder.path) ===
      getPathWithCountryCode(countryCode, path)
    );
  }, [countryCode, data.payloadSampleOrder, path]);

  const heroLevel = generateHeroLevel(heroType, enhancedBreadcrumbs);
  const heroProps: HeroProps | SpotlightHeroProps = generateHeroProps(
    title,
    heroLevel,
    subtitle,
    featuredVideo,
    featuredMedia,
    cta?.[0],
    theme
  );

  const isDarkThemed = heroType === "Spotlight" || heroLevel !== 3;
  const breadcrumbsNode = (
    <BackToResults isDarkThemed={isDarkThemed}>
      <Breadcrumbs
        data={enhancedBreadcrumbs}
        isDarkThemed={isDarkThemed}
        data-testid="simple-page-breadcrumbs-top"
      />
    </BackToResults>
  );

  const pageData: PageData = {
    breadcrumbs: enhancedBreadcrumbs,
    signupBlock: signupBlock,
    meta: isSimplePageProtected
      ? {
          ...meta,
          noIndex: true
        }
      : meta,
    path
  };

  const isHeroKeyLine = Boolean(isBrandProviderEnabled && brandLogo);

  return (
    <Protected isPageProtected={isSimplePageProtected}>
      <Page
        brand={brandLogo}
        title={title}
        pageData={pageData}
        siteData={data.payloadSite}
        microCopy={data.allPayloadMicroCopy.nodes}
        pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
        visualiserHouseTypes={data.allPayloadHouseModel.nodes}
        homePage={data.payloadHomePage}
        generalError={data.payloadGeneralError}
        countryNavigation={data.payloadCountryNavigation}
        sampleOrder={data.payloadSampleOrder}
        visualiser={data.payloadVisualiser}
        variantCodeToPathMap={pageContext?.variantCodeToPathMap}
        ogImageUrl={featuredMedia?.sizes.heroDesktop.url}
        pageId={pageContext.pageId}
        payloadPageId={_id}
      >
        {renderHero(heroProps, breadcrumbsNode, heroType, {
          isHeroKeyLine: isHeroKeyLine
        })}
        <TableOfContent>
          {shareWidget && shareWidget[0] && (
            <ShareWidgetSection
              data={shareWidget[0]}
              data-testid="share-widget-section-top"
            />
          )}
          {leadBlock && leadBlock[0] && (
            <LeadBlockSection data={leadBlock[0]} />
          )}
          {sections && (
            <Sections data={sections} startIndex={+!!leadBlock?.[0]} />
          )}
          {isSampleOrderPage && data.payloadSampleOrder && (
            <SampleBasketSection
              data={{
                richTextDescription:
                  data.payloadSampleOrder.richTextDescription,
                checkoutFormSection:
                  data.payloadSampleOrder.checkoutFormSection,
                emptyBasketMessage: data.payloadSampleOrder.emptyBasketMessage,
                browseProductsCTALabel:
                  data.payloadSampleOrder.browseProductsCTALabel,
                browseProductsCTA: data.payloadSampleOrder.browseProductsCTA
              }}
            />
          )}
          {linkColumns && linkColumns[0] && (
            <LinkColumnsSection data={linkColumns[0]} />
          )}
          {shareWidget && shareWidget[0] && (
            <ShareWidgetSection
              data={shareWidget[0]}
              data-testid="share-widget-section-bottom"
            />
          )}
          {nextBestActions && <NextBestActions data={nextBestActions} />}
          <Section
            backgroundColor="alabaster"
            isSlim
            data-testid="breadcrumbs-section-bottom"
          >
            <BackToResults>
              <Breadcrumbs
                data={enhancedBreadcrumbs}
                data-testid="simple-page-breadcrumbs-bottom"
              />
            </BackToResults>
          </Section>
        </TableOfContent>
      </Page>
    </Protected>
  );
};

export default SimplePage;

export const pageQuery = graphql`
  query SimplePageById($pageId: String!) {
    payloadSimplePage(id: { eq: $pageId }) {
      _id
      ...PageInfoHeroFragment
      ...PayloadSimplePageFragment
      breadcrumbTitle
      heroType
      isSimplePageProtected
      cta {
        ...LinkFragment
      }
      leadBlock {
        ...LeadBlockFragment
      }
      date
      shareWidget {
        ...ShareWidgetFragment
      }
      sections {
        ...SectionsFragment
      }
      nextBestActions {
        value {
          ...NextBestActionsFragment
        }
      }
      linkColumns {
        ...LinkColumnsSectionFragment
      }
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
      ...SampleBasketSectionFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
